import React, { ReactNode } from 'react';

import { Button, Grid, Tooltip } from '@material-ui/core';

import { useStyles } from './Themable.hooks';

import TranslationHelper from '../../helpers/TranslationHelper';

export const ITEMS_TOGGLE_LIMIT = 10000;

export interface IListObjectInitialState<T> {
    unassignedObjects: T[];
    assignedObjects: T[];
    filteredAssignedObjects: T[];
    originalAssignedObjects: T[];
}

export interface IBaseTransferListObject {
    relationId: string;
    type?: string;
    name: string;
}

interface IOwnProps<T> {
    objectLists: IListObjectInitialState<T>;
    checked: T[];
    customComponents: { leftList: ReactNode; rightList: ReactNode };
    intersection: (a: T[], b: T[]) => T[];
    handleAdd: (toAdd: T[]) => void;
    handleRemove: (toRemove: T[]) => void;
}

export const TransferList = <T extends IBaseTransferListObject>({
    objectLists,
    checked,
    customComponents,
    intersection,
    handleAdd,
    handleRemove,
}: IOwnProps<T>) => {
    const { assignedObjects, unassignedObjects, filteredAssignedObjects } =
        objectLists;
    const classes = useStyles();

    const leftChecked = intersection(checked, unassignedObjects);
    const rightChecked = intersection(checked, assignedObjects);

    const handleAllRight = () => {
        handleAdd(unassignedObjects);
    };

    const handleAllLeft = () => {
        handleRemove(filteredAssignedObjects);
    };

    const handleCheckedRight = () => {
        handleAdd(leftChecked);
    };

    const handleCheckedLeft = () => {
        handleRemove(rightChecked);
    };

    return (
        <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
            className={classes.root}
        >
            <Grid className={classes.gridItem} item>
                {customComponents.leftList}
            </Grid>
            <Grid className={classes.gridItemButtons} item>
                <Grid container direction="column" alignItems="center">
                    <Tooltip
                        title={TranslationHelper.translate('Move all right')}
                    >
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                className={classes.button}
                                onClick={handleAllRight}
                                disabled={
                                    unassignedObjects.length === 0 ||
                                    unassignedObjects.length >
                                        ITEMS_TOGGLE_LIMIT
                                }
                                aria-label="move all right"
                            >
                                ≫
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip
                        title={TranslationHelper.translate(
                            'Move selected right'
                        )}
                    >
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                className={classes.button}
                                onClick={handleCheckedRight}
                                disabled={leftChecked.length === 0}
                                aria-label="move selected right"
                            >
                                &gt;
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip
                        title={TranslationHelper.translate(
                            'Move selected left'
                        )}
                    >
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                className={classes.button}
                                onClick={handleCheckedLeft}
                                disabled={rightChecked.length === 0}
                                aria-label="move selected left"
                            >
                                &lt;
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip
                        title={TranslationHelper.translate('Move all left')}
                    >
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                className={classes.button}
                                onClick={handleAllLeft}
                                disabled={
                                    filteredAssignedObjects.length === 0 ||
                                    filteredAssignedObjects.length >
                                        ITEMS_TOGGLE_LIMIT
                                }
                                aria-label="move all left"
                            >
                                ≪
                            </Button>
                        </span>
                    </Tooltip>
                </Grid>
            </Grid>
            <Grid className={classes.gridItem} item>
                {customComponents.rightList}
            </Grid>
        </Grid>
    );
};
